import React, { useState } from 'react';
import { Card, CardContent, Skeleton, Typography, Button } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import InventoryItemCard from './InventoryItemCard';
import { GroupedInventoryItem, SelectedGroupedItem } from '../types/Inventory';
import ItemDetailLightModal from '../../../skin-db/offer-details/ItemDetailLightModal';

interface InventoryGridProps {
  loading: boolean;
  groupedItems: GroupedInventoryItem[];
  selectedItems: SelectedGroupedItem[];
  currencySymbol: string;
  onToggleSelectItem: (group: GroupedInventoryItem) => void;
  onResetFilters: () => void;
  tradeable?: boolean;
}

const InventoryGrid: React.FC<InventoryGridProps> = ({
                                                       loading,
                                                       groupedItems,
                                                       selectedItems,
                                                       currencySymbol,
                                                       onToggleSelectItem,
                                                       onResetFilters,
                                                       tradeable
                                                     }) => {
  const navigate = useNavigate();

  // State to manage the universal item details light modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedGroupForModal, setSelectedGroupForModal] = useState<GroupedInventoryItem | null>(null);

  const handleOpenDetails = (group: GroupedInventoryItem) => {
    setSelectedGroupForModal(group);
    setDetailModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailModalOpen(false);
    setSelectedGroupForModal(null);
  };

  if (tradeable === false) {
    return (
        <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto px-6 h-full">
          <ErrorOutlineIcon sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />

          <div className="text-white font-bold mb-2 text-xl">
            We are unable to load your inventory due to your Steam Account settings.
          </div>

          <Button
              variant="contained"
              className="bg-dark-primary hover:bg-dark-primary/80 normal-case font-bold px-8 py-2 rounded-md"
              onClick={() => navigate('/profile/connect-account')}
          >
            Settings
          </Button>
        </div>
    );
  }

  if (groupedItems.length === 0 && !loading) {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-center h-full">
          <SearchOffIcon sx={{ fontSize: 80, color: '#4b5563', mb: 2 }} />
          <Typography variant="h5" className="text-white font-bold mb-2">No items found</Typography>
          <Button variant="contained" className="bg-dark-primary hover:bg-dark-primary/80 normal-case font-bold px-8" onClick={onResetFilters}>View all items</Button>
        </div>
    );
  }

  return (
      <>
        <div className={`grid gap-4 pb-4 w-full ${groupedItems.length === 1 ? 'justify-items-center' : ''} grid-cols-2 lg:grid-cols-[repeat(auto-fill,minmax(240px,1fr))]`}>
          {loading ? [...Array(12)].map((_, i) => (
              <Card key={`skeleton-${i}`} className="bg-[#1c1f26] border border-gray-700">
                <CardContent className="flex flex-col !p-3">
                  <Skeleton width="80%" height={30} />
                  <Skeleton width="60%" height={20} className="mt-1" />
                  <Skeleton variant="rectangular" height={160} className="my-3 rounded-md" />
                  <Skeleton width="40%" height={25} className="mb-2" />
                  <div className="h-[40px] w-full"><Skeleton width="70%" height={10} className="rounded-full" /></div>
                  <Skeleton variant="rectangular" height={36} className="mt-auto rounded-md" />
                </CardContent>
              </Card>
          )) : groupedItems.map((group) => (
              <InventoryItemCard
                  key={group.key}
                  group={group}
                  selected={selectedItems.some((i) => i.key === group.key)}
                  onToggle={onToggleSelectItem}
                  currencySymbol={currencySymbol}
                  onOpenDetails={handleOpenDetails}
              />
          ))}
        </div>

        <ItemDetailLightModal
            open={detailModalOpen}
            onClose={handleCloseDetails}
            item={(selectedGroupForModal?.representative as any) ?? null}
        />
      </>
  );
};

export default InventoryGrid;
